import React, { useState } from "react";
import { SeguimientoEmail, Settings } from "@/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff, Mail, Check, Clock, XCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

export default function SeguimientoPanel({ leadId }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: seguimientos = [], isLoading } = useQuery({
    queryKey: ['seguimientos', leadId],
    queryFn: () => SeguimientoEmail.list({ lead_id: leadId }, { orderBy: 'created_date', orderDirection: 'desc' }),
    enabled: !!leadId,
  });

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => Settings.list(),
  });

  // Parsear configuración de seguimientos
  const configSeguimientos = React.useMemo(() => {
    const config = settings.find(s => s.clave === 'config_seguimientos')?.valor;
    if (config) {
      try {
        return JSON.parse(config);
      } catch (e) {
        return [{ dias: 2, prompt: '' }, { dias: 4, prompt: '' }];
      }
    }
    return [{ dias: 2, prompt: '' }, { dias: 4, prompt: '' }];
  }, [settings]);

  const updateSeguimientoMutation = useMutation({
    mutationFn: ({ id, data }) => SeguimientoEmail.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seguimientos', leadId] });
    },
  });

  const handleToggleSeguimiento = async (seguimiento) => {
    const nuevoEstado = !seguimiento.activo;
    await updateSeguimientoMutation.mutateAsync({
      id: seguimiento.id,
      data: { 
        activo: nuevoEstado,
        motivo_desactivacion: nuevoEstado ? null : 'Desactivado manualmente',
        fecha_cancelacion: nuevoEstado ? null : new Date().toISOString()
      }
    });
    
    toast({
      title: nuevoEstado ? "Seguimiento activado" : "Seguimiento desactivado",
      description: nuevoEstado 
        ? "Se reanudarán los emails de seguimiento" 
        : "No se enviarán más emails de seguimiento",
    });
  };

  const seguimientoActivo = seguimientos.find(s => s.activo);
  const seguimientosHistorico = seguimientos.filter(s => !s.activo);

  if (isLoading) {
    return (
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardContent className="p-4">
          <div className="animate-pulse h-20 bg-gray-200 dark:bg-gray-800 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (seguimientos.length === 0) {
    return (
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
            <BellOff className="w-4 h-4" />
            No hay seguimiento automático configurado
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calcularProximoEmail = (seguimiento) => {
    if (!seguimiento.activo) return null;
    
    const emailsEnviados = seguimiento.emails_enviados?.length || 0;
    if (emailsEnviados >= configSeguimientos.length) return null;
    
    const proximoConfig = configSeguimientos[emailsEnviados];
    if (!proximoConfig) return null;
    
    const fechaPresupuesto = new Date(seguimiento.fecha_envio_presupuesto);
    const fechaProximo = new Date(fechaPresupuesto);
    fechaProximo.setDate(fechaProximo.getDate() + proximoConfig.dias);
    
    return {
      numero: emailsEnviados + 1,
      fecha: fechaProximo,
      dias: proximoConfig.dias
    };
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="w-4 h-4" />
          Seguimiento Automático
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {seguimientoActivo && (
          <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                Seguimiento activo
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {seguimientoActivo.activo ? 'Activado' : 'Desactivado'}
                </span>
                <Switch
                  checked={seguimientoActivo.activo}
                  onCheckedChange={() => handleToggleSeguimiento(seguimientoActivo)}
                />
              </div>
            </div>
            
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <p>Presupuesto enviado: {formatDate(seguimientoActivo.fecha_envio_presupuesto)}</p>
              
              {/* Mostrar emails enviados */}
              {seguimientoActivo.emails_enviados?.map((email, idx) => (
                <div key={idx} className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <Check className="w-3 h-3" />
                  Email {email.numero} enviado: {formatDate(email.fecha_envio)}
                </div>
              ))}
              
              {/* Mostrar próximo email */}
              {seguimientoActivo.activo && (() => {
                const proximo = calcularProximoEmail(seguimientoActivo);
                if (proximo) {
                  return (
                    <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 mt-2">
                      <Clock className="w-3 h-3" />
                      Próximo email ({proximo.numero}): {formatDate(proximo.fecha)} ({proximo.dias} días)
                    </div>
                  );
                } else if ((seguimientoActivo.emails_enviados?.length || 0) >= configSeguimientos.length) {
                  return (
                    <div className="flex items-center gap-1 text-gray-500 mt-2">
                      <Check className="w-3 h-3" />
                      Todos los emails de seguimiento enviados
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        )}

        {/* Historial de seguimientos anteriores */}
        {seguimientosHistorico.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Historial</p>
            {seguimientosHistorico.slice(0, 3).map((seg) => (
              <div 
                key={seg.id} 
                className="p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-400"
              >
                <div className="flex items-center justify-between">
                  <span>
                    {seg.cancelado_por_respuesta ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <Mail className="w-3 h-3" /> Cliente respondió
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-orange-600">
                        <XCircle className="w-3 h-3" /> {seg.motivo_desactivacion || 'Desactivado'}
                      </span>
                    )}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => handleToggleSeguimiento(seg)}
                  >
                    Reactivar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}